// Avatar palette assignment — Pass 1.5 fix.
//
// The original hash-based assignment (avatarPaletteIndex in
// src/lib/theme.ts) collides hard on real Slack user_ids: see
// docs/AVATAR_HASH_INVESTIGATION.md. Birthday-paradox math says any
// simple hash will frequently fail to give distinct indices when ≤ 6
// users hash into 6 buckets. The fix here is structural rather than
// algorithmic — assign by sorted position.
//
// Properties:
// - Distinct indices for every user when there are ≤ 6 users.
// - Even distribution (floor(N/6) ≤ count ≤ ceil(N/6) per index) for
//   larger workspaces.
// - Stable within a workspace: same user_id always lands on the same
//   index across sessions, restores, theme switches.
// - NOT stable across workspaces: a user might get a different color
//   in a different workspace because their position in the sorted
//   user list differs. Documented and accepted — the user_id itself
//   is preserved on the avatar element for tooling that cares.

import type { UsersMap } from "../types/slack";

const PALETTE_SIZE = 6;

export { PALETTE_SIZE };

// Returns user_id → palette_index for every entry in `users`. The
// assignment is deterministic given the same input set.
export function assignAvatarPaletteIndices(
  users: UsersMap,
): Record<string, number> {
  const ids = Object.keys(users).sort();
  const out: Record<string, number> = {};
  for (let i = 0; i < ids.length; i++) {
    out[ids[i]!] = i % PALETTE_SIZE;
  }
  return out;
}

// Fallback for cases where a workspace map isn't available — synthetic
// test data, messages whose `user` is unknown, etc. The hash bias from
// the investigation still applies, but a fallback is better than no
// color at all. Same algorithm as the legacy avatarPaletteIndex so
// existing snapshots don't shift.
export function fallbackAvatarPaletteIndex(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = ((h << 5) - h + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % PALETTE_SIZE;
}

// Resolve a user_id → palette_index using the workspace's prebuilt
// map first, falling back to the hash if the user_id isn't in the
// map (rare — only happens for user_ids not in the users dict).
export function resolveAvatarPaletteIndex(
  paletteByUserID: Record<string, number>,
  userID: string | undefined,
  fallbackSeed: string,
): number {
  if (userID && userID in paletteByUserID) {
    return paletteByUserID[userID]!;
  }
  return fallbackAvatarPaletteIndex(fallbackSeed);
}
